import React, { useState, useEffect } from 'react';
import './App.css';
import cx from 'classnames';
import { createBrowserRouter, Link } from 'react-router-dom';
import { Widget } from '@typeform/embed-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import './i18n';
import LanguageSelector from './components/LanguageSelector';

/* Section: Navigation Bar */
const Navbar = ({ hasBackground }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSectionClick = (e, sectionId) => {
    setIsMenuOpen(false);
    if (window.location.pathname === '/' && sectionId) {
      e.preventDefault();
      const element = document.querySelector(sectionId);
      if (element) {
        // Add a small delay to ensure the menu is closed before scrolling
        setTimeout(() => {
          const headerOffset = window.innerWidth <= 768 ? 60 : 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }, 100);
      }
    } else if (!sectionId) {
      e.preventDefault();
      if (window.location.pathname !== '/') {
        // Get the current language from i18n
        const currentLanguage = i18n.language;
        // Navigate to homepage while preserving the language
        window.location.href = `/?lang=${currentLanguage}#`;
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <div className={cx('navbar', { hasBackground })}>
      <div className="navbar-max">
        <div className="logo">
          <div>
            <Link to="/#" onClick={(e) => handleSectionClick(e)} className="logo-link">
              {t('navbar.home')}
            </Link>
          </div>
        </div>
        <div className={cx('navbar-links', { active: isMenuOpen })}>
          <div>
            <Link to="/#Produtos" onClick={(e) => handleSectionClick(e, '#Produtos')}>
              {t('navbar.products')}
            </Link>
          </div>
          <div>
            <Link to="/#Qualidade" onClick={(e) => handleSectionClick(e, '#Qualidade')}>
              {t('navbar.quality')}
            </Link>
          </div>
          <div>
            <Link to="/#Floresta" onClick={(e) => handleSectionClick(e, '#Floresta')}>
              {t('navbar.forest')}
            </Link>
          </div>
          <div>
            <Link to="/#Sustentabilidade" onClick={(e) => handleSectionClick(e, '#Sustentabilidade')}>
              {t('navbar.sustainability')}
            </Link>
          </div>
          <div>
            <Link to="/quem-somos" onClick={() => setIsMenuOpen(false)}>
              {t('navbar.aboutUs')}
            </Link>
          </div>
          <div>
            <Link to="/contactos" onClick={() => setIsMenuOpen(false)}>
              {t('navbar.contacts')}
            </Link>
          </div>
          <LanguageSelector />
        </div>
        <div className={cx('mobile-menu-toggle', { active: isMenuOpen })} onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
};

const renderHTML = (htmlString) => {
  return <span dangerouslySetInnerHTML={{ __html: htmlString }} />;
};

const Section = ({ id, name, content, style, invert, fluid, column, hero, flat }) => {
  return (
    <div id={id || name} className={cx('section', { invert, fluid, column, flat })}>
      <div className={cx('section-image', { column })} style={style}>
        {hero}
      </div>
      <div className={cx('section-info', { column })}>
        {name && <div className="section-title">{typeof name === 'string' ? renderHTML(name) : name}</div>}
        {content && (
          <div className="section-content">{typeof content === 'string' ? renderHTML(content) : content}</div>
        )}
      </div>
    </div>
  );
};

/* Section: Footer */
const Footer = () => {
  const { t } = useTranslation();

  const handleClick = (e, sectionId) => {
    if (window.location.pathname === '/' && sectionId) {
      e.preventDefault();
      const element = document.querySelector(sectionId);
      if (element) {
        const headerOffset = window.innerWidth <= 768 ? 60 : 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    } else if (!sectionId) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="footer">
      <div>
        <Link to="/" onClick={(e) => handleClick(e)}>
          {t('footer.company')}
        </Link>
      </div>
      <div className="footer-right">
        <div className="footer-right-up">
          <div>
            <Link to="/#Produtos" onClick={(e) => handleClick(e, '#Produtos')}>
              {t('navbar.products')}
            </Link>
          </div>
          <div>
            <Link to="/#Qualidade" onClick={(e) => handleClick(e, '#Qualidade')}>
              {t('navbar.quality')}
            </Link>
          </div>
          <div>
            <Link to="/#Floresta" onClick={(e) => handleClick(e, '#Floresta')}>
              {t('navbar.forest')}
            </Link>
          </div>
          <div>
            <Link to="/#Sustentabilidade" onClick={(e) => handleClick(e, '#Sustentabilidade')}>
              {t('navbar.sustainability')}
            </Link>
          </div>
        </div>
        <div className="footer-right-down">
          <div>
            <Link to="/politica-de-privacidade" onClick={(e) => handleClick(e)}>
              {t('footer.privacyPolicy')}
            </Link>
          </div>
          <div>
            <Link to="/quem-somos" onClick={(e) => handleClick(e)}>
              {t('navbar.aboutUs')}
            </Link>
          </div>
          <div>
            <Link to="/quem-somos" onClick={(e) => handleClick(e)}>
              {t('footer.mission')}
            </Link>
          </div>
          <div>
            <Link to="/contactos" onClick={(e) => handleClick(e)}>
              {t('navbar.contacts')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      // Check if the page is scrolled to the top
      const scrolledToTop = window.scrollY === 0;

      // Update state based on the scroll position
      setIsScrolledToTop(scrolledToTop);
    };

    // Add the scroll event listener when the component mounts
    window.addEventListener('scroll', handleScroll);

    // Remove the scroll event listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div>
      <Navbar hasBackground={!isScrolledToTop} />

      {children}
      <Footer />
    </div>
  );
};

const LayoutWithHeader = ({ name, image, children }) => {
  return (
    <div className="layout-with-header">
      <Navbar hasBackground={true} />
      <div className="layout-with-header-content">
        <div
          className="layoutHeader"
          style={{
            backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0)), linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0)), url("${image}")`,
          }}
        >
          {name}
        </div>
        <div className="max-width">{children}</div>
      </div>
      <Footer />
    </div>
  );
};

/* Section: Expandable Card Component */
const ExpandableCard = ({ isOpen, onClose, product }) => {
  const { t } = useTranslation();

  if (!isOpen || !product) return null;

  return (
    <div className={cx('card-overlay', { active: isOpen })} onClick={onClose}>
      <div className="expanded-card" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        <div className="expanded-card-content">
          <img src={product.image} alt={t(`products.${product.id}`)} className="expanded-card-image" />
          <h2 className="expanded-card-title">{t(`products.${product.id}`)}</h2>
          <div className="expanded-card-description">{t(`products.${product.id}.description`)}</div>
        </div>
      </div>
    </div>
  );
};

/* Section: Homepage */
const Home = () => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeIndices, setActiveIndices] = useState([0, 0, 0]); // Track active index for each carousel

  useEffect(() => {
    // Handle hash navigation when the component mounts or hash changes
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    // Initial check for hash
    handleHashChange();

    // Add event listener for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleScroll = (groupIndex, event, group) => {
    const container = event.target;
    const cardWidth = container.offsetWidth;
    const scrollPosition = container.scrollLeft;
    const activeIndex = Math.round(scrollPosition / cardWidth);
    const maxScroll = container.scrollWidth - container.offsetWidth;

    // Check if we're at the last card
    if (scrollPosition >= maxScroll) {
      // Prevent further scrolling
      container.scrollLeft = maxScroll;
      setActiveIndices((prev) => {
        const newIndices = [...prev];
        newIndices[groupIndex] = group.length - 1;
        return newIndices;
      });
    } else {
      setActiveIndices((prev) => {
        const newIndices = [...prev];
        newIndices[groupIndex] = activeIndex;
        return newIndices;
      });
    }
  };

  const scrollToCard = (groupIndex, cardIndex) => {
    const container = document.querySelector(`.carousel-container:nth-child(${groupIndex + 1})`);
    if (container) {
      const cardWidth = container.offsetWidth;
      container.scrollTo({
        left: cardIndex * cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const products = [
    { id: 'natural', image: '/Rolha Natural.webp' },
    { id: 'colmated', image: '/Rolha Colmatada.webp' },
    { id: 'capsulated', image: '/Rolha Capsulada.webp' },
    { id: 'onePlusOne', image: '/Rolha 1+1.webp' },
    { id: 'evolution10', image: '/Rolha Evolution 10.webp' },
    { id: 'alpha', image: '/Rolha Alpha.webp' },
    { id: 'agglomerated', image: '/Rolha Aglomerada.webp' },
    { id: 'champagne', image: '/Rolha Champagne.webp' },
    { id: 'champagneEvolution', image: '/Rolha Champagne Evolution.webp' },
  ];

  const handleCardClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseCard = () => {
    setSelectedProduct(null);
  };

  // Split products into three groups for mobile carousels
  const productGroups = [
    // Natural Corks group
    [
      { id: 'natural', image: '/Rolha Natural.webp' },
      { id: 'colmated', image: '/Rolha Colmatada.webp' },
      { id: 'capsulated', image: '/Rolha Capsulada.webp' },
    ],
    // Technical Corks group (including Alpha)
    [
      { id: 'onePlusOne', image: '/Rolha 1+1.webp' },
      { id: 'evolution10', image: '/Rolha Evolution 10.webp' },
      { id: 'alpha', image: '/Rolha Alpha.webp' },
      { id: 'agglomerated', image: '/Rolha Aglomerada.webp' },
    ],
    // Champagne Corks group (without Alpha)
    [
      { id: 'champagne', image: '/Rolha Champagne.webp' },
      { id: 'champagneEvolution', image: '/Rolha Champagne Evolution.webp' },
    ],
  ];

  return (
    <Layout>
      <div className="hero" style={{ backgroundImage: `url('/background.webp')` }}>
        <div className="nossa">{t('hero.title')}</div>
        <div className="desde">{t('hero.subtitle')}</div>
      </div>
      <Section
        id="Qualidade"
        name={t('quality.title')}
        content={t('quality.content')}
        style={{ backgroundImage: `url('/qualidade.webp')` }}
      />
      <Section
        id="Floresta"
        invert
        name={t('forest.title')}
        content={t('forest.content')}
        style={{ backgroundImage: `url('/floresta.webp')` }}
      />
      <Section
        id="Sustentabilidade"
        name={t('sustainability.title')}
        content={t('sustainability.content')}
        style={{ backgroundImage: `url('/sustentabilidade.webp')` }}
      />
      {/* Section: Produtos (Cards Section) */}
      <div id="Produtos" className="cards-section">
        <h2 className="cards-title">{t('products.title')}</h2>
        <div className="cards-container">
          {window.innerWidth <= 480
            ? // Mobile view with three carousels
              productGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="carousel-section">
                  <h3 className="carousel-title">
                    {t(`products.group${groupIndex === 1 ? 3 : groupIndex === 2 ? 2 : 1}`)}
                  </h3>
                  <div className="carousel-container" onScroll={(e) => handleScroll(groupIndex, e, group)}>
                    {group.map((product) => (
                      <motion.div
                        key={product.id}
                        className="card"
                        onClick={() => handleCardClick(product)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <img src={product.image} alt={t(`products.${product.id}`)} className="card-image" />
                        <div className="card-content">
                          <h3 className="card-title">{t(`products.${product.id}`)}</h3>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="carousel-dots">
                    {group.map((_, index) => (
                      <div
                        key={index}
                        className={`carousel-dot ${activeIndices[groupIndex] === index ? 'active' : ''}`}
                        onClick={() => scrollToCard(groupIndex, index)}
                      />
                    ))}
                  </div>
                </div>
              ))
            : // Desktop view with grid layout
              products.map((product) => (
                <motion.div
                  key={product.id}
                  className="card"
                  onClick={() => handleCardClick(product)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={product.image} alt={t(`products.${product.id}`)} className="card-image" />
                  <div className="card-content">
                    <h3 className="card-title">{t(`products.${product.id}`)}</h3>
                  </div>
                </motion.div>
              ))}
        </div>
      </div>
      <ExpandableCard isOpen={!!selectedProduct} onClose={handleCloseCard} product={selectedProduct} />
    </Layout>
  );
};

/* Section: Quem Somos */
const QuemSomos = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LayoutWithHeader image="/quemsomos.webp">
      <div className="quemsomos-content">
        <h2 className="section-title">{renderHTML(t('aboutUs.title'))}</h2>
        <div className="section-content">{renderHTML(t('aboutUs.content'))}</div>
        <h3 className="section-title" style={{ marginTop: '40px', fontSize: 'clamp(24px, 4vh, 32px)' }}>
          {renderHTML(t('aboutUs.mission.title'))}
        </h3>
        <div className="section-content">
          <p>{renderHTML(t('aboutUs.mission.mission'))}</p>
          <p>{renderHTML(t('aboutUs.mission.vision'))}</p>
          <p>
            <strong style={{ fontSize: 'clamp(24px, 4vh, 32px)', display: 'block', marginBottom: '20px' }}>
              {renderHTML(t('aboutUs.mission.qualityPolicy'))}
            </strong>
          </p>
          <p>{renderHTML(t('aboutUs.mission.qualityPolicyIntro'))}</p>
          <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
            {t('aboutUs.mission.points', { returnObjects: true }).map((point, index) => (
              <li key={index}>{renderHTML(point)}</li>
            ))}
          </ul>
          <p>
            <strong style={{ fontSize: 'clamp(24px, 4vh, 32px)', display: 'block', marginBottom: '20px' }}>
              {renderHTML(t('aboutUs.mission.fscPolicy'))}
            </strong>
          </p>
          <p>{renderHTML(t('aboutUs.mission.fscPolicyIntro'))}</p>
          <ul>
            {t('aboutUs.mission.fscPolicyBullets')
              .split('\n')
              .map((bullet, index) => (
                <li key={index}>{renderHTML(bullet.trim())}</li>
              ))}
          </ul>
          <p>{renderHTML(t('aboutUs.mission.fscPolicyConclusion'))}</p>
        </div>
        <h2 className="section-title projetos-title">{t('aboutUs.projects')}</h2>
        <div className="projetos-image">
          <img src="/paineis.webp" alt="Projetos" />
        </div>
        <h2 className="section-title certificados-title">{t('aboutUs.certificates')}</h2>
        <div className="certificados-pdf">
          <iframe
            src={i18n.language === 'pt' ? '/ISO_9001_2025_PT.pdf' : '/ISO_9001_2025_EN.pdf'}
            title="Certificados"
            width="100%"
            height="800px"
          />
        </div>
        <div className="certificados-pdf">
          <iframe
            src={i18n.language === 'pt' ? '/SYSTECODE_2025_PT.pdf' : '/SYSTECODE_2025_EN.pdf'}
            width="100%"
            height="800px"
          />
        </div>
        <div className="certificados-pdf">
          <iframe src="/COC-150842-FSC.pdf" title="FSC Certificate" width="100%" height="800px" />
        </div>
      </div>
    </LayoutWithHeader>
  );
};

/* Section: Contactos */
const Contactos = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LayoutWithHeader image="/contactos.webp" name={t('contacts.title')}>
      <div className="contact-info-container">
        <div className="contact-details">
          <p>J.A. VEIGA DE MACEDO, S.A.</p>
          <p>📍: Rua Santa Maria 1185, 4535-401 SANTA MARIA DE LAMAS, PORTUGAL</p>
          <p>📞: (+351) 227 442 025</p>
          <p>📧: geral@javeigademacedo.com</p>
        </div>
        <div className="map-container">
          <iframe
            title="J.A. Veiga de Macedo Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.95899957196!2d-8.572249723338171!3d40.98237862101824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd247f357e87518f%3A0x182bedea797fb2e7!2sJ.A%20Veiga%20de%20Macedo%2C%20S.A.!5e0!3m2!1spt-PT!2spt!4v1742320232641!5m2!1spt-PT!2spt"
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
      <Widget id="ghg9JtSJ" className="my-form" />
    </LayoutWithHeader>
  );
};

/* Section: Politica */
const Politica = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = t('privacy.content');
  const sections = content.split('\n\n').map((section) => {
    const lines = section.split('\n');
    const title = lines[0];
    const content = lines.slice(1).join('\n');
    return { title, content };
  });

  return (
    <div className="layout-with-header">
      <Navbar hasBackground={true} />
      <div className="layout-with-header-content">
        <div className="layoutHeader privacy">{t('privacy.title')}</div>
        <div className="max-width">
          <div className="privacy-content">
            {sections.map((section, index) => (
              <div key={index}>
                {section.title === 'Direitos do Titular dos Dados Pessoais' ? (
                  <>
                    <h2 className="section-title">{section.title}</h2>
                    <div className="section-content">
                      {section.content.split('\n\n').map((paragraph, pIndex) => (
                        <div key={pIndex}>{pIndex === 0 ? paragraph : <p>{paragraph}</p>}</div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="section-title">{section.title}</h2>
                    <div className="section-content">{section.content}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/quem-somos',
    element: <QuemSomos />,
  },
  {
    path: '/contactos',
    element: <Contactos />,
  },
  {
    path: '/politica-de-privacidade',
    element: <Politica />,
  },
]);

export default router;
